// Posting (ERAs) fixtures (POSTING_HANDOFF §12, revised). Copy is from the boards.
import type {
  AutopilotRow,
  BankDifference,
  ClaimCandidate,
  Actor,
  LineItem,
  LogStep,
  PaymentRef,
  Posting,
  PostingItem,
} from './types';
import type { PostedHistoryRow } from './historyMock';
import { PMS } from '@/features/shared/pms';
import { cardChargedLine, money } from './utils';

export const POSTED_LAST_30 = { count: 38, total: 9846.2, month: 'October' } as const;
export const TRAINING = { confirmed: 38, milestone: 100 } as const;

/** The default treating dentist: in network with every payer here but United Concordia (Settings › Networks). */
export const DR_CARTER = 'Dr. Emily Carter';
export const DR_LEE = 'Dr. Marcus Lee';
const EFT: PaymentRef = { method: 'eft' };

/**
 * Training (Posting handoff §7.1, H1 §6): each kind of payment needs 5 confirmed before
 * autopilot covers it. `confirmed` is what the practice has confirmed so far; the store
 * adds one for every posting a person confirms.
 */
export const KIND_MINIMUM = 5;
export interface TrainingKind {
  id: string;
  /** In a sentence: "payments the payer sent to the patient". */
  label: string;
  confirmed: number;
}
export function seedTrainingKinds(): TrainingKind[] {
  return [
    { id: 'nothing_unusual', label: 'payments with nothing unusual', confirmed: 18 },
    { id: 'claim_not_found', label: `payments ${PMS} couldn’t tie to a claim`, confirmed: 5 },
    { id: 'lines_differ', label: 'payments paid as a different procedure', confirmed: 5 },
    { id: 'reversal', label: 'payments taken back and paid again', confirmed: 5 },
    { id: 'partial', label: 'partial payments', confirmed: 5 },
    { id: 'held_two_plans', label: 'payments for patients with two plans', confirmed: 5 },
    { id: 'paid_after_closed', label: 'payments after a claim was closed', confirmed: 2 },
    { id: 'takeback', label: 'takebacks', confirmed: 3 },
    { id: 'interest', label: 'interest payments', confirmed: 5 },
    { id: 'paid_to_patient', label: 'payments the payer sent to the patient', confirmed: 0 },
    { id: 'patient_credit', label: 'payments that leave a patient credit', confirmed: 0 },
  ];
}
export const LAST_READ = '12 minutes ago';

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** 24h → "9:58 AM" (US 12h). */
function clock(h: number, m: number): string {
  const hr = ((h + 11) % 12) + 1;
  return `${String(hr)}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

/** A plausible, payer-shaped member id (no more "——" on Posted detail, §8). */
function memberFor(payerShort: string, seed: number): string {
  const s = Math.abs(seed);
  const d = (n: number, len: number): string => String(s % n).padStart(len, '0');
  switch (payerShort) {
    case 'Cigna': return `U${d(100, 2)}-${d(1000, 3)}-${d(1000, 3)}`;
    case 'MetLife': return `553-${d(100, 2)}-${d(10000, 4)}-01`;
    case 'Aetna': return `W${String(2200000 + (s % 700000))}`;
    case 'Guardian': return String(7100000 + (s % 800000));
    case 'United Concordia': return `UC${String(40200000 + (s % 700000))}`;
    default: return String(80100000 + (s % 1800000)); // Delta-shaped 8 digits
  }
}

/** A full (never-truncated) ERA trace number, "1T740582213"-shaped (§8). */
function traceFor(seed: number): string {
  const s = Math.abs(seed);
  return `1T${String(700000000 + (s % 299999999))}`;
}

function seedOf(str: string): number {
  let n = 0;
  for (let i = 0; i < str.length; i++) n = (n * 31 + str.charCodeAt(i)) | 0;
  return n;
}

function line(code: string, tooth: string | undefined, desc: string, fee: number, allowed: number, paid: number): LineItem {
  const writeOff = Math.round((fee - allowed) * 100) / 100;
  const owes = Math.round((allowed - paid) * 100) / 100;
  return { code, ...(tooth ? { tooth } : {}), desc, fee, allowed, paid, writeOff, owes };
}

function genLog(payer: string, eraDisplay: string, amount: number): LogStep[] {
  const a = `$${Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  return [
    { time: '10:41:58', text: `Opened Office Manager › Batch Processor and the ${payer} ERA of ${eraDisplay}` },
    { time: '10:42:06', text: `Matched the payment to the claim` },
    { time: '10:42:13', text: `Entered the paid amount and write-off` },
    { time: '10:42:16', text: 'Posted' },
    { time: '10:42:21', text: `Checked: the claim shows Received; the balance moved by ${a}` },
  ];
}

// ---------------------------------------------------------------------------
// To post — 9 items, net $2,422.00
// ---------------------------------------------------------------------------

export function buildToPost(): PostingItem[] {
  return [
    // 1 · Jorge — claim not found (Needs you)
    {
      id: 'PO-1',
      claimOf: 'Sep 22',
      patient: 'Jorge Martinez',
      firstName: 'Jorge',
      payer: { name: 'Delta Dental of Texas', short: 'Delta' },
      memberId: '81447302',
      kind: 'claim_not_found',
      group: 'needs_you',
      dentist: DR_CARTER,
      payment: EFT,
      amount: 486,
      amountLabel: 'Delta paid',
      eraDate: '2026-10-08',
      eraDisplay: 'Oct 8, 2026',
      depositedDisplay: 'Oct 8',
      trace: '1T740582213',
      listMeta: 'ERA of Oct 8 · 7 days',
      reason: `${PMS} couldn’t find the claim`,
      whatHappened: {
        headline: `${PMS} couldn’t tie this payment to a claim`,
        body: `Delta paid $486.00, but ${PMS} marked it Not Found. ELVA found a claim of Sep 22 that matches on everything but the claim number, which Delta cut short.`,
      },
      compare: [
        { label: 'Patient', era: 'J MARTINEZ', claim: 'Jorge Martinez', verdict: 'Same', ok: true },
        { label: 'Member ID', era: '81447302', claim: '81447302', verdict: 'Same', ok: true },
        { label: 'Visit', era: 'Sep 22, 2026', claim: 'Sep 22, 2026', verdict: 'Same', ok: true },
        { label: 'Procedure', era: 'D2740, tooth #3', claim: 'D2740, tooth #3', verdict: 'Same', ok: true },
        { label: 'Billed', era: '$1,180.00', claim: '$1,180.00', verdict: 'Same', ok: true },
        { label: 'Claim number', era: '4471', claim: '44718', verdict: 'Cut short', ok: false },
      ],
      breakdown: [
        { label: 'Delta paid', amount: 486 },
        { label: 'Write-off', amount: 208 },
        { label: 'Jorge owes', amount: 486 },
        { label: 'Fee for the Sep 22 claim', amount: 1180, kind: 'total' },
      ],
      question: {
        text: 'Is this Jorge’s claim of Sep 22?',
        options: [
          { id: 'yes', label: 'Yes, it’s this claim', effect: 'enable_post' },
          { id: 'different', label: 'No, it’s a different claim', effect: 'search' },
          { id: 'not_ours', label: 'Not our patient', effect: 'not_our_patient' },
        ],
        meta: 'Yes enables Post. “Not our patient” explains how to return the money.',
      },
      notOurPatientNote:
        'Delta paid you by mistake. ELVA can return it or let Delta take it back from a future payment.',
      sources: [
        { id: 's-era-1', label: 'ERA of Oct 8' },
        { id: 's-claim-1', label: 'Claim of Sep 22' },
        { id: 's-fee-1', label: 'Your Delta fee schedule' },
      ],
      history: [
        `ERA received in ${PMS} Oct 8`,
        `${PMS} marked it Not Found`,
        'ELVA found the Sep 22 claim Oct 8',
      ],
      lines: [line('D2740', '#3', 'porcelain crown', 1180, 972, 486)],
      balanceBefore: 0,
      balanceAfter: 486,
      how: 'agent',
      log: [
        { time: '10:41:58', text: 'Opened Office Manager › Batch Processor and the Delta Dental ERA of Oct 8' },
        { time: '10:42:04', text: 'Chose Select Claim on the line J MARTINEZ · $486.00 (marked Not Found)' },
        { time: '10:42:09', text: 'Picked Jorge Martinez · Sep 22, 2026 · $1,180.00' },
        { time: '10:42:13', text: 'Entered paid $486.00 and write-off $208.00 on D2740 #3' },
        { time: '10:42:16', text: 'Posted' },
        { time: '10:42:21', text: 'Checked: the claim shows Received; Jorge’s balance is $486.00' },
      ],
      durationSec: 23,
      postedTicks: [
        { text: 'Checked after posting: the claim shows Received, the write-off is $208.00, and Jorge’s balance is $486.00.' },
        { text: 'Delta’s deposit of Oct 8 ($2,904.10) is now fully posted.' },
      ],
      learning: {
        text: 'Delta Dental of Texas cuts claim numbers to 4 digits. From now on, ELVA matches Delta payments by member ID, visit date and fee, and asks you only when more than one claim fits.',
        scope: 'Kept as your office’s experience with Delta Dental of Texas.',
      },
      depositNote: 'Delta’s deposit of Oct 8 ($2,904.10) is now fully posted.',
    },

    // 2 · Ethan — paid as a different procedure (Needs you)
    {
      id: 'PO-2',
      patient: 'Ethan Walsh',
      firstName: 'Ethan',
      payer: { name: 'Cigna DPPO', short: 'Cigna' },
      memberId: 'U41-118-907',
      kind: 'lines_differ',
      group: 'needs_you',
      dentist: DR_CARTER,
      payment: EFT,
      amount: 380,
      amountLabel: 'Cigna paid',
      eraDate: '2026-10-13',
      eraDisplay: 'Oct 13, 2026',
      depositedDisplay: 'Oct 13',
      trace: '1T772044118',
      listMeta: 'ERA of Oct 13 · 2 days',
      reason: 'Paid as a different crown',
      whatHappened: {
        headline: 'Cigna paid it as a metal crown, not the porcelain crown you billed',
        body: `Ethan’s plan pays crowns on back teeth at the price of a full metal crown. This is the plan’s rule, not an underpayment. ${PMS} held it because the code paid isn’t the code billed.`,
      },
      breakdown: [
        { label: 'Billed D2740 porcelain crown', detail: 'your fee', amount: 1180 },
        { label: 'Cigna paid 50% of $760.00', detail: 'its price for a metal crown (D2791)', amount: 380 },
        { label: 'Write-off', detail: 'your fee − Cigna’s allowed $905.00 for D2740', amount: 275 },
        { label: 'Ethan owes', detail: '$905.00 − $380.00', amount: 525, kind: 'total' },
      ],
      question: {
        text: 'Post it this way?',
        options: [
          { id: 'yes', label: 'Yes, post it this way', effect: 'enable_post' },
          { id: 'appeal', label: 'Appeal it', effect: 'appeal' },
          { id: 'wrong', label: 'Something’s wrong', effect: 'something_wrong' },
        ],
        meta: 'Before the visit, Ethan was told about $380. He’ll now owe $525. “Appeal it” sends it to Denied & underpaid with the X-ray and narrative, and nothing is posted yet.',
      },
      sources: [
        { id: 's-era-2', label: 'ERA of Oct 13' },
        { id: 's-plan-2', label: 'Ethan’s plan: crowns on molars' },
        { id: 's-fee-2', label: 'Your Cigna fee schedule' },
      ],
      history: [`ERA received in ${PMS} Oct 13`, 'ELVA read the alternate-benefit rule Oct 13'],
      lines: [line('D2740', '#30', 'porcelain crown (paid as D2791)', 1180, 905, 380)],
      balanceBefore: 0,
      balanceAfter: 525,
      how: 'agent',
      log: genLog('Cigna', 'Oct 13', 380),
      durationSec: 19,
      postedTicks: [{ text: 'Checked after posting: Ethan’s balance is $525.00.' }],
    },

    // 3 · Linda — reversal (Needs you)
    {
      id: 'PO-3',
      claimOf: 'Sep 8',
      patient: 'Linda Park',
      firstName: 'Linda',
      payer: { name: 'Delta Dental of Texas', short: 'Delta' },
      memberId: '60918844',
      kind: 'reversal',
      group: 'needs_you',
      dentist: DR_CARTER,
      payment: EFT,
      amount: -28,
      amountLabel: 'Net change',
      eraDate: '2026-10-08',
      eraDisplay: 'Oct 8, 2026',
      depositedDisplay: 'Oct 8',
      trace: '1T740581990',
      listMeta: 'ERA of Oct 8 · 7 days',
      reason: 'Taken back and paid again',
      whatHappened: {
        headline: 'Delta took back $214.00 and paid $186.00 instead',
        body: `Delta paid Linda’s Sep 8 claim on Sep 25. On Oct 8 it reprocessed the claim: it had paid her exam at 100%, but her plan pays exams at 80%. ${PMS} marked the ERA Invalid because of the negative line.`,
      },
      breakdown: [
        { label: 'Paid Sep 25', detail: 'already posted', amount: 214 },
        { label: 'Taken back Oct 8', amount: -214, kind: 'neg' },
        { label: 'Paid again Oct 8', amount: 186 },
        { label: 'Linda now owes more', amount: 28, kind: 'total' },
      ],
      check: {
        ok: true,
        text: 'Delta is right: Linda’s plan pays the periodic exam (D0120) at 80%, and 20% of the $140.00 allowed is $28.00. If ELVA can’t tell why from the ERA and Delta’s portal, it calls Delta first and puts the answer here.',
      },
      question: {
        text: 'What do you want to do?',
        options: [
          { id: 'post', label: 'Post both lines', effect: 'enable_post' },
          { id: 'dispute', label: 'Dispute it with Delta', effect: 'dispute' },
        ],
        meta: 'Codes: claim status 22 (reversal), PR-2 (coinsurance).',
      },
      sources: [
        { id: 's-era-3', label: 'ERA of Oct 8' },
        { id: 's-portal-3', label: 'Delta’s portal' },
      ],
      history: ['Paid Sep 25', 'Reversed and repaid on the ERA of Oct 8', 'ELVA checked the reason Oct 8'],
      lines: [line('D0120', undefined, 'periodic exam', 140, 140, 112), line('D1110', undefined, 'cleaning', 96, 74, 74)],
      balanceBefore: 0,
      balanceAfter: 28,
      how: 'agent',
      log: [
        { time: '10:41:58', text: 'Opened the Delta ERA of Oct 8 (marked Invalid)' },
        { time: '10:42:05', text: 'Posted the takeback of −$214.00 on Linda’s Sep 8 claim' },
        { time: '10:42:12', text: 'Posted the new payment of $186.00 on the same claim' },
        { time: '10:42:18', text: 'Checked: Linda’s balance went up by $28.00' },
      ],
      durationSec: 20,
      postedTicks: [{ text: 'Checked after posting: Linda’s balance went up by $28.00.' }],
    },

    // 4 · Sofia — nothing unusual (Ready)
    readyItem({
      id: 'PO-4',
      patient: 'Sofia Romero',
      firstName: 'Sofia',
      payer: { name: 'Delta Dental of Texas', short: 'Delta' },
      memberId: '30177645',
      amount: 164,
      amountLabel: 'Delta paid',
      eraDate: '2026-10-13',
      eraDisplay: 'Oct 13, 2026',
      listMeta: 'ERA of Oct 13 · 2 days',
      claimOf: 'Oct 6',
      headline: 'Nothing unusual',
      ticks: [
        { text: 'Found the claim: Sofia Romero, Oct 6, D1110 and D0120.' },
        { text: 'Delta’s allowed amounts match your Delta fee schedule.' },
        { text: 'Paid, write-off and Sofia’s part add up to the fee.' },
      ],
      breakdown: [
        { label: 'Delta paid 100% of $164.00 allowed', amount: 164 },
        { label: 'Write-off', detail: 'your fee $236.00 − $164.00', amount: 72 },
        { label: 'Sofia owes', amount: 0, kind: 'total' },
      ],
      lines: [line('D1110', undefined, 'cleaning', 140, 100, 100), line('D0120', undefined, 'periodic exam', 96, 64, 64)],
      balanceAfter: 0,
    }),

    // 5 · Ivy — nothing unusual (Ready)
    readyItem({
      id: 'PO-5',
      patient: 'Ivy Chen',
      firstName: 'Ivy',
      payer: { name: 'MetLife', short: 'MetLife' },
      memberId: '553-61-2290-01',
      amount: 96,
      amountLabel: 'MetLife paid',
      eraDate: '2026-10-14',
      eraDisplay: 'Oct 14, 2026',
      listMeta: 'ERA of Oct 14 · 1 day',
      claimOf: 'Oct 5',
      headline: 'Nothing unusual',
      ticks: [
        { text: 'Found the claim: Ivy Chen, Oct 5, D0274.' },
        { text: 'MetLife’s allowed amount matches your fee schedule.' },
        { text: 'Paid, write-off and Ivy’s part add up to the fee.' },
      ],
      breakdown: [
        { label: 'MetLife paid 100% of $96.00 allowed', amount: 96 },
        { label: 'Write-off', detail: 'your fee $120.00 − $96.00', amount: 24 },
        { label: 'Ivy owes', amount: 0, kind: 'total' },
      ],
      lines: [line('D0274', undefined, 'four bitewing X-rays', 120, 96, 96)],
      balanceAfter: 0,
    }),

    // 6 · Omar — secondary to send (Ready)
    readyItem({
      id: 'PO-6',
      patient: 'Omar Haddad',
      firstName: 'Omar',
      payer: { name: 'Aetna PPO', short: 'Aetna' },
      memberId: 'W2207741',
      amount: 640,
      amountLabel: 'Aetna paid',
      eraDate: '2026-10-09',
      eraDisplay: 'Oct 9, 2026',
      listMeta: 'ERA of Oct 9 · 6 days',
      claimOf: 'Sep 24',
      headline: 'Aetna paid. Then the secondary claim to Delta Dental goes out',
      underBar: 'After posting, ELVA prepares the secondary claim with Aetna’s EOB attached. It appears on Claims to review.',
      ticks: [
        { text: 'Found the claim: Omar Haddad, Sep 24, D2392 ×2.' },
        { text: 'Aetna is primary; Delta Dental of Texas is secondary.' },
      ],
      breakdown: [
        { label: 'Aetna paid', amount: 640 },
        { label: 'Then: secondary to Delta Dental of Texas', amount: 0, kind: 'total' },
      ],
      lines: [line('D2392', '#19', 'two-surface filling', 360, 320, 320), line('D2392', '#20', 'two-surface filling', 360, 320, 320)],
      balanceAfter: 0,
    }),

    // 7 · Maya — partial (Ready)
    readyItem({
      id: 'PO-7',
      patient: 'Maya Singh',
      firstName: 'Maya',
      payer: { name: 'MetLife', short: 'MetLife' },
      memberId: '553-90-1187-01',
      amount: 318,
      amountLabel: 'MetLife paid',
      eraDate: '2026-10-12',
      eraDisplay: 'Oct 12, 2026',
      listMeta: 'ERA of Oct 12 · 3 days',
      claimOf: 'Sep 30',
      headline: 'MetLife paid 2 of 3 procedures. The third is still being reviewed',
      underBar: 'ELVA posts the two paid lines and leaves the claim open for D0330.',
      ticks: [
        { text: 'Found the claim: Maya Singh, Sep 30.' },
        { text: 'D0150 and D0210 are paid; D0330 is still under review.' },
      ],
      breakdown: [
        { label: 'D0150 · comprehensive exam', amount: 118 },
        { label: 'D0210 · full X-ray series', amount: 200 },
        { label: 'Total paid', amount: 318, kind: 'total' },
      ],
      lines: [line('D0150', undefined, 'comprehensive exam', 130, 118, 118), line('D0210', undefined, 'full X-ray series', 220, 200, 200)],
      balanceAfter: 0,
    }),

    // 8 · Chloe — two plans (Ready)
    readyItem({
      id: 'PO-8',
      patient: 'Chloe Bennett',
      firstName: 'Chloe',
      payer: { name: 'Guardian', short: 'Guardian' },
      memberId: '7781520',
      amount: 212,
      amountLabel: 'Guardian paid',
      eraDate: '2026-10-13',
      eraDisplay: 'Oct 13, 2026',
      listMeta: 'ERA of Oct 13 · 2 days',
      claimOf: 'Oct 1',
      headline: 'Chloe has two plans. Guardian is primary',
      underBar: 'ELVA checked the order of the plans. After posting, the secondary claim goes to Claims to review.',
      ticks: [
        { text: 'Found the claim: Chloe Bennett, Oct 1, D1110 and D0274.' },
        { text: 'Guardian is primary; the spouse’s plan is secondary.' },
      ],
      breakdown: [
        { label: 'Guardian paid', amount: 212 },
        { label: 'Then: secondary claim to the spouse’s plan', amount: 0, kind: 'total' },
      ],
      lines: [line('D1110', undefined, 'cleaning', 140, 110, 110), line('D0274', undefined, 'four bitewing X-rays', 120, 102, 102)],
      balanceAfter: 0,
    }),

    // 9 · Marcus — paid after closed (Ready)
    readyItem({
      id: 'PO-9',
      patient: 'Marcus Hill',
      firstName: 'Marcus',
      payer: { name: 'United Concordia', short: 'United Concordia' },
      memberId: 'UC40981773',
      amount: 154,
      amountLabel: 'United Concordia paid',
      eraDate: '2026-10-07',
      eraDisplay: 'Oct 7, 2026',
      listMeta: 'ERA of Oct 7 · 8 days',
      claimOf: 'Aug 18',
      headline: 'Paid after the claim was closed',
      underBar: 'ELVA reopens the claim, posts the payment and closes it again.',
      ticks: [
        { text: `The Aug 18 claim was closed in ${PMS} on Sep 30 with $0.` },
        { text: 'United Concordia paid $154.00 on Oct 7.' },
      ],
      breakdown: [
        { label: 'United Concordia paid', amount: 154 },
        { label: 'Marcus owes', amount: 0, kind: 'total' },
      ],
      lines: [line('D4341', undefined, 'scaling and root planing', 240, 154, 154)],
      balanceAfter: 0,
    }),

    // 10 · Iris — the payer paid the patient (D197). Ready.
    paidToPatientItem(),

    // D238/D249: ELVA wrote the posting but couldn't confirm it in the PMS — two stages.
    pmsCheckItem('checking'),
    pmsCheckItem('question'),
    // D291: a write-off for a dentist whose network status with the payer isn't confirmed.
    networkQuestionItem(),

    // 11 · Hugo — the payment leaves a patient credit (D198). Ready.
    patientCreditItem(),

    // 12–16 · The 5 claims from the Delta deposit of Oct 9 (Bank BD-1). ELVA
    // fetched the remittance itself and dropped them here (rcm-19 §8). Ready.
    ...bd1Claim('PO-BD1-1', 'Hannah Cole', 'Sep 15', 286.0, 'D2740', '#8', 'porcelain crown'),
    ...bd1Claim('PO-BD1-2', 'Isaac Ford', 'Sep 19', 190.4, 'D2392', '#19', 'two-surface filling'),
    ...bd1Claim('PO-BD1-3', 'Grace Lin', 'Sep 23', 342.0, 'D2750', '#14', 'crown'),
    ...bd1Claim('PO-BD1-4', 'Leo Barnes', 'Sep 27', 248.0, 'D4341', undefined, 'scaling and root planing'),
    ...bd1Claim('PO-BD1-5', 'Mia Russo', 'Sep 30', 246.0, 'D0150', undefined, 'comprehensive exam'),
  ];
}


/**
 * D238 / D249: ELVA wrote a posting and couldn't confirm it in the PMS.
 * `checking`: ELVA checks again by itself, nothing for a person (1 of 3 checks).
 * `question`: 3 checks passed without an answer, so a person says whether it is there.
 */
function pmsCheckItem(stage: 'checking' | 'question'): PostingItem {
  const asking = stage === 'question';
  const patient = asking ? 'Maria Santos' : 'Tomas Rivera';
  const base = readyItem({
    id: asking ? 'PO-ASK' : 'PO-CHK',
    patient,
    firstName: patient.split(' ')[0] ?? patient,
    payer: asking ? { name: 'Delta Dental of Texas', short: 'Delta' } : { name: 'Cigna DPPO', short: 'Cigna' },
    memberId: memberFor(asking ? 'Delta' : 'Cigna', seedOf(patient)),
    amount: asking ? 198 : 264,
    amountLabel: asking ? 'Delta paid' : 'Cigna paid',
    eraDate: asking ? '2026-10-12' : '2026-10-14',
    eraDisplay: asking ? 'Oct 12, 2026' : 'Oct 14, 2026',
    listMeta: asking ? 'ERA of Oct 12 · 3 days' : 'ERA of Oct 14 · 1 day',
    claimOf: asking ? 'Oct 2' : 'Oct 7',
    headline: '',
    ticks: [],
    breakdown: [],
    lines: asking
      ? [line('D2391', '#14', 'one-surface filling', 290, 198, 198)]
      : [line('D2392', '#3', 'two-surface filling', 360, 264, 264)],
    balanceAfter: 0,
  });
  const checks = asking ? 3 : 1;
  const ask: PostingItem['question'] = {
    text: `Is this payment in ${PMS}?`,
    options: [
      { id: 'there', label: 'It’s there', effect: 'pms_there' },
      { id: 'not_there', label: 'It isn’t there', effect: 'pms_not_there' },
      { id: 'there_wrong', label: 'It’s there but wrong', effect: 'something_wrong' },
    ],
    meta: `Open the patient’s ledger in ${PMS} and look for the ${asking ? '$198.00' : '$264.00'} payment. What you see is recorded as what you saw, not as proof. “It isn’t there” doesn’t post again on its own.`,
  };
  return {
    ...base,
    kind: 'pms_unconfirmed',
    group: asking ? 'needs_you' : 'checking',
    reason: asking ? `Is this payment in ${PMS}?` : `Checking with ${PMS}`,
    whatHappened: asking
      ? {
          headline: `ELVA couldn’t confirm this payment in ${PMS}`,
          body: `ELVA wrote this posting but couldn’t confirm it in ${PMS}. It checked ${String(checks)} times without an answer, so it’s asking you before it does anything else.`,
        }
      : {
          headline: `Checking with ${PMS}`,
          body: `ELVA wrote this posting but couldn’t confirm it in ${PMS}. It’s checking again before doing anything else.`,
        },
    pmsCheck: { stage, checks },
    ...(asking ? { question: ask } : {}),
    underBar: asking ? `ELVA won’t post this again until you say whether it’s in ${PMS}.` : 'Nothing for you to do. ELVA checks again by itself.',
    history: [`ERA received in ${PMS} ${asking ? 'Oct 12' : 'Oct 14'}`, `ELVA wrote the posting; ${PMS} didn’t confirm it`, `ELVA checked ${PMS} ${String(checks)} ${checks === 1 ? 'time' : 'times'}`],
  };
}

/** D291: Humana paid Dr. Brooks's patient; her network status with Humana is "Not confirmed" (Settings › Networks). */
function networkQuestionItem(): PostingItem {
  const lines = [line('D2740', '#14', 'porcelain crown', 1180, 890, 450)];
  const base = readyItem({
    id: 'PO-HUM',
    patient: 'Daniel Okafor',
    firstName: 'Daniel',
    payer: { name: 'Humana', short: 'Humana' },
    memberId: String(61200000 + (Math.abs(seedOf('Daniel Okafor')) % 800000)),
    amount: 450,
    amountLabel: 'Humana paid',
    eraDate: '2026-10-13',
    eraDisplay: 'Oct 13, 2026',
    listMeta: 'ERA of Oct 13 · 2 days',
    claimOf: 'Oct 1',
    headline: '',
    ticks: [],
    breakdown: [],
    lines,
    balanceAfter: 440,
    dentist: 'Dr. Olivia Brooks',
  });
  const writeOff = round2(lines.reduce((t, l) => t + l.writeOff, 0));
  return {
    ...base,
    kind: 'network_unconfirmed',
    group: 'needs_you',
    reason: 'Is Dr. Brooks in network with Humana?',
    whatHappened: {
      headline: 'Is Dr. Olivia Brooks in network with Humana?',
      body: `The posting needs a ${money(writeOff)} contractual write-off, and nobody has confirmed Dr. Brooks’s network status with Humana. ELVA holds it until you say.`,
    },
    networkQ: { dentist: 'Dr. Olivia Brooks', payer: 'Humana' },
    question: {
      text: 'Is Dr. Olivia Brooks in network with Humana?',
      options: [
        { id: 'in', label: 'In network', effect: 'network_in' },
        { id: 'out', label: 'Out of network', effect: 'network_out' },
      ],
      meta: 'In network: ELVA writes off the difference to the contract. Out of network: no write-off, and the patient owes what Humana doesn’t pay. Your answer updates Settings › Practice details › Networks.',
    },
    underBar: 'ELVA holds postings that need a write-off until the network status is confirmed.',
  };
}

/** Iris Calder: MetLife sent its $580.00 to Iris, not the practice (D197, board PostingPaidToPatient). */
function paidToPatientItem(): PostingItem {
  const lines = [line('D3330', '#30', 'root canal, molar', 1450, 1450, 0)];
  return {
    id: 'PO-IRIS',
    patient: 'Iris Calder',
    firstName: 'Iris',
    payer: { name: 'MetLife', short: 'MetLife' },
    memberId: '553-40-7781-01',
    kind: 'paid_to_patient',
    group: 'ready',
    dentist: DR_LEE,
    payment: { method: 'eob', eobDate: 'Oct 12' },
    claimId: 'CLM-28820',
    claimOf: 'Sep 30',
    paidToPatient: { amount: 580, eobLabel: 'EOB Oct 12' },
    fee: 1450,
    visitPaid: 500,
    procedureLine: 'D3330 · root canal, molar · #30',
    codes: 'CARC 100 · payment made to the patient',
    amount: 0,
    amountLabel: 'MetLife paid you',
    eraDate: '2026-10-12',
    eraDisplay: 'Oct 12, 2026',
    depositedDisplay: 'no deposit',
    trace: '',
    headMeta: 'EOB of Oct 12 · read from the paper EOB · no deposit',
    listMeta: 'EOB of Oct 12 · 3 days',
    reason: 'Paid to the patient',
    whatHappened: {
      headline: 'MetLife paid Iris, not the practice',
      body: 'Dr. Lee is out of network with MetLife and there’s no assignment of benefits on file for Iris, so MetLife sent its $580.00 to her. Iris owes the practice the rest of the fee.',
    },
    breakdown: [],
    depositNote: 'MetLife sent its $580.00 to Iris, so no deposit arrives for this claim.',
    underBar: `ELVA posts it in ${PMS}, then checks the claim and the balance.`,
    sources: [
      { id: 's-eob-iris', label: 'MetLife EOB, Oct 12' },
      { id: 's-net-iris', label: 'Dr. Lee’s networks' },
      { id: 's-ins-iris', label: `Iris’s insurance in ${PMS}: no assignment on file` },
    ],
    history: ['EOB faxed Oct 12', 'read by ELVA Oct 12'],
    lines,
    balanceBefore: 950,
    balanceAfter: 950,
    how: 'agent',
    log: [
      { time: '10:41:58', text: `Opened Iris Calder’s claim of Sep 30 in ${PMS}` },
      { time: '10:42:06', text: 'Posted a $0.00 MetLife payment with the note “MetLife paid the patient $580.00 · EOB Oct 12”' },
      { time: '10:42:13', text: 'Closed the claim' },
      { time: '10:42:19', text: 'Checked: the claim is closed and Iris’s balance is $950.00' },
    ],
    durationSec: 21,
    postedTicks: [{ text: 'Checked after posting: the claim is closed, MetLife’s payment shows $0.00, and Iris’s balance is $950.00.' }],
  };
}

/** Hugo Brandt: Guardian paid, and Hugo had already paid the full fee (D198, board PostingCredit). */
function patientCreditItem(): PostingItem {
  const lines = [line('D3330', '#19', 'root canal, molar', 1450, 1450, 612)];
  return {
    id: 'PO-HUGO',
    patient: 'Hugo Brandt',
    firstName: 'Hugo',
    payer: { name: 'Guardian', short: 'Guardian' },
    memberId: '7781902',
    kind: 'patient_credit',
    group: 'ready',
    dentist: DR_LEE,
    payment: { method: 'check', checkNo: '77134406' },
    claimOf: 'Sep 25',
    fee: 1450,
    visitPaid: 1450,
    credit: { sinceIso: '2026-10-15', pronoun: 'he' },
    procedureLine: 'D3330 · root canal, molar · #19',
    amount: 612,
    amountLabel: 'Guardian paid you',
    eraDate: '2026-10-13',
    eraDisplay: 'Oct 13, 2026',
    depositedDisplay: 'Oct 13',
    trace: '77134406',
    headMeta: 'Paper check 77134406 of Oct 13 · faxed Oct 13',
    listMeta: 'check 77134406 · 2 days',
    reason: 'Patient credit',
    whatHappened: {
      headline: 'Guardian paid. Hugo had already paid the full fee, so $612.00 becomes credit on his account',
      body: '',
    },
    breakdown: [],
    underBar: `ELVA posts it in ${PMS}, then checks the claim and the balance.`,
    sources: [
      { id: 's-chk-hugo', label: 'Guardian check 77134406' },
      { id: 's-pay-hugo', label: `Hugo’s payment of Sep 25 in ${PMS}` },
      { id: 's-net-hugo', label: 'Dr. Lee’s networks' },
    ],
    history: ['Paper check faxed Oct 13', 'read by ELVA Oct 13'],
    lines,
    balanceBefore: 0,
    balanceAfter: -612,
    how: 'agent',
    log: [
      { time: '10:41:58', text: `Opened Guardian check 77134406 and Hugo Brandt’s claim of Sep 25 in ${PMS}` },
      { time: '10:42:06', text: 'Posted Guardian’s $612.00 on D3330 #19 with no write-off' },
      { time: '10:42:13', text: 'Wrote the reference “Guardian check 77134406”' },
      { time: '10:42:19', text: 'Checked: the claim shows Received; Hugo’s account shows $612.00 in credit' },
    ],
    durationSec: 21,
    postedTicks: [{ text: 'Checked after posting: the claim shows Received, and Hugo’s account shows $612.00 in credit.' }],
  };
}

/** One ready To-post item for a claim ELVA pulled from the Delta BD-1 remittance. */
function bd1Claim(
  id: string,
  patient: string,
  visit: string,
  paid: number,
  code: string,
  tooth: string | undefined,
  desc: string,
): [PostingItem] {
  const fee = round2(paid * 1.45);
  return [
    readyItem({
      id,
      patient,
      firstName: patient.split(' ')[0] ?? patient,
      payer: { name: 'Delta Dental of Texas', short: 'Delta' },
      memberId: memberFor('Delta', seedOf(`${patient}|${id}`)),
      amount: paid,
      amountLabel: 'Delta paid',
      eraDate: '2026-10-10',
      eraDisplay: 'Oct 10, 2026',
      listMeta: `ERA of Oct 10 · from the Delta deposit of Oct 9`,
      claimOf: visit,
      headline: `From the Delta deposit of Oct 9 · ${visit} visit`,
      underBar: 'ELVA fetched this remittance from Delta’s portal after the deposit landed with no ERA.',
      ticks: [
        { text: `Found the claim: ${patient}, ${visit}.` },
        { text: 'Delta’s allowed amounts match your Delta fee schedule.' },
      ],
      breakdown: [],
      lines: [line(code, tooth, desc, fee, paid, paid)],
      balanceAfter: 0,
    }),
  ];
}

interface ReadyInput {
  id: string;
  patient: string;
  firstName: string;
  payer: { name: string; short: string };
  memberId: string;
  amount: number;
  amountLabel: string;
  eraDate: string;
  eraDisplay: string;
  listMeta: string;
  claimOf: string;
  headline: string;
  underBar?: string;
  ticks: { text: string }[];
  breakdown: PostingItem['breakdown'];
  lines: LineItem[];
  balanceAfter: number;
  dentist?: string;
  payment?: PaymentRef;
}

function readyItem(i: ReadyInput): PostingItem {
  // The breakdown always shows fee, write-off and patient owes, built from the
  // posted lines so it adds up (fix on staging, rcm-19 §8). Fee = paid + write-off + owes.
  const feeSum = round2(i.lines.reduce((s, l) => s + l.fee, 0));
  const paidSum = round2(i.lines.reduce((s, l) => s + l.paid, 0));
  const writeOffSum = round2(i.lines.reduce((s, l) => s + l.writeOff, 0));
  const owesSum = round2(i.lines.reduce((s, l) => s + l.owes, 0));
  const breakdown: PostingItem['breakdown'] = [
    { label: 'Your fee', amount: feeSum },
    { label: `${i.payer.short} paid`, amount: paidSum },
    { label: 'Write-off', amount: writeOffSum },
    { label: `${i.firstName} owes`, amount: owesSum, kind: 'total' },
  ];
  return {
    id: i.id,
    patient: i.patient,
    firstName: i.firstName,
    payer: i.payer,
    memberId: i.memberId,
    kind: 'nothing_unusual',
    group: 'ready',
    dentist: i.dentist ?? DR_CARTER,
    payment: i.payment ?? EFT,
    claimOf: i.claimOf,
    amount: i.amount,
    amountLabel: i.amountLabel,
    eraDate: i.eraDate,
    eraDisplay: i.eraDisplay,
    depositedDisplay: i.eraDisplay.replace(', 2026', ''),
    trace: '1T7' + i.id.replace(/\D/g, '') + '00044',
    listMeta: i.listMeta,
    reason: 'Nothing unusual',
    whatHappened: { headline: i.headline, body: '' },
    ticks: i.ticks,
    breakdown,
    ...(i.underBar ? { underBar: i.underBar } : {}),
    sources: [{ id: `s-${i.id}`, label: `ERA of ${i.eraDisplay.replace(', 2026', '')}` }],
    history: [`ERA received in ${PMS}`, `ELVA checked it`],
    lines: i.lines,
    balanceBefore: 0,
    balanceAfter: i.balanceAfter,
    how: 'agent',
    log: genLog(i.payer.short, i.eraDisplay.replace(', 2026', ''), i.amount),
    durationSec: 18,
    postedTicks: [{ text: `Checked after posting: ${i.firstName}’s claim shows Received.` }],
  };
}

/** Jorge's "different claim" search candidates (§4.1). */
export const JORGE_CANDIDATES: ClaimCandidate[] = [
  { id: 'c-jm-922', patient: 'Jorge Martinez', payer: 'Delta Dental of Texas', serviceDate: 'Sep 22, 2026', procedure: 'D2740, tooth #3', billed: 1180, claimNumber: '44718' },
  { id: 'c-jm-815', patient: 'Jorge Martinez', payer: 'Delta Dental of Texas', serviceDate: 'Aug 15, 2026', procedure: 'D1110, cleaning', billed: 118, claimNumber: '43902' },
  { id: 'c-rm-918', patient: 'Rosa Medina', payer: 'Delta Dental of Texas', serviceDate: 'Sep 18, 2026', procedure: 'D2740, tooth #14', billed: 1180, claimNumber: '44655' },
];

// ---------------------------------------------------------------------------
// Posted report (§12.2)
// ---------------------------------------------------------------------------

export const POSTED_SUMMARY = {
  month: 'October',
  count: 38,
  total: 9846.2,
  undone: 1,
  autopilotLine: '112 posted on autopilot, 52 confirmed by your team',
  trainingLine: `All posted in ${PMS} by ELVA, each one confirmed by your team. 1 undone.`,
} as const;

const AVA: Actor = { name: 'Ava Thompson', org: 'Lone Star Billing' };
const RYAN: Actor = { name: 'Ryan Cole', org: 'Lone Star Billing' };

/** The uploaded statement covers Oct 1–14; anything on/after Oct 15 isn't on it yet. */
const STATEMENT_LAST_DAY = 14;

function posting(p: {
  id: string;
  patient: string;
  payer: { name: string; short: string };
  amount: number;
  /** day of the month in October 2026 (for ISO + statement-cover check). */
  mday: number;
  h: number;
  m: number;
  /** "Today" / "Yesterday" / "Oct 9" */
  dateLabel: string;
  day: Posting['postedDay'];
  how?: 'agent' | 'api';
  by?: Actor | 'autopilot' | 'self';
  claimOf?: string;
  lines?: LineItem[];
  undone?: boolean;
  isUndo?: boolean;
  reverseNote?: string;
}): Posting {
  const lines = p.lines ?? [line('D2740', '#3', 'porcelain crown', round2(p.amount * 2), round2(p.amount * 1.4), p.amount)];
  const how = p.how ?? 'agent';
  const feeSum = round2(lines.reduce((s, l) => s + l.fee, 0));
  const owesSum = round2(lines.reduce((s, l) => s + l.owes, 0));
  const seed = seedOf(`${p.patient}|${p.payer.short}|${p.id}`);
  const postedTime = clock(p.h, p.m);
  const dateDisp = p.mday === 16 ? 'Oct 16' : p.mday === 15 ? 'Oct 15' : `Oct ${String(p.mday)}`;
  const postedIso = `2026-10-${String(p.mday).padStart(2, '0')}T${String(p.h).padStart(2, '0')}:${String(p.m).padStart(2, '0')}:00`;
  // Each posting ties to ITS payer's own deposit, or says no statement covers it yet (§8).
  const covered = p.mday <= STATEMENT_LAST_DAY;
  const depositNote = covered
    ? `Part of a ${p.payer.short} deposit on ${dateDisp} into Chase ··4417.`
    : `No bank statement covers ${dateDisp} yet.`;
  return {
    id: p.id,
    patient: p.patient,
    firstName: p.patient.split(' ')[0] ?? p.patient,
    payer: p.payer,
    memberId: memberFor(p.payer.short, seed),
    claimOf: p.claimOf ?? 'this month',
    dentist: DR_CARTER,
    payment: { method: 'eft', trace: traceFor(seed) },
    amount: p.amount,
    amountLabel: `${p.payer.short} paid`,
    postedTime,
    postedDateLabel: p.dateLabel,
    postedIso,
    postedDay: p.day,
    eraDisplay: dateDisp,
    trace: traceFor(seed),
    confirmedBy: p.by ?? AVA,
    how,
    lines,
    balanceBefore: feeSum,
    balanceAfter: owesSum,
    howFound: `${p.payer.short} paid the claim; ELVA matched it and posted it in ${PMS}.`,
    sources: [{ id: `src-${p.id}`, label: `ERA of ${dateDisp}` }],
    log: genLog(p.payer.short, dateDisp, p.amount),
    durationSec: 21,
    ...(how === 'api'
      ? {
          apiRecords: [
            { record: 'Claim', id: '44812' },
            { record: 'Insurance payment', id: 'PMT-90233' },
            { record: 'Adjustment', id: 'ADJ-11890' },
          ],
        }
      : {}),
    depositNote,
    statementCovers: covered,
    history: [`posted by ELVA ${p.dateLabel === 'Today' ? 'today' : p.dateLabel} at ${postedTime}`],
    ...(p.undone ? { undone: true } : {}),
    ...(p.isUndo ? { isUndo: true } : {}),
    ...(p.reverseNote ? { reverseNote: p.reverseNote } : {}),
  };
}

const DELTA = { name: 'Delta Dental of Texas', short: 'Delta' };
const CIGNA = { name: 'Cigna', short: 'Cigna' };
const GUARDIAN = { name: 'Guardian', short: 'Guardian' };
const AETNA = { name: 'Aetna', short: 'Aetna' };
const METLIFE = { name: 'MetLife', short: 'MetLife' };
const UC = { name: 'United Concordia', short: 'United Concordia' };
const PAYERS = [DELTA, CIGNA, GUARDIAN, AETNA, METLIFE, UC];
const FIRST = ['Grace', 'Leo', 'Mia', 'Noah', 'Ella', 'Finn', 'Ruby', 'Jack', 'Nora', 'Owen', 'Ivy', 'Cole', 'Zoe', 'Max', 'Lily', 'Eli', 'Ava', 'Sam', 'Nina', 'Theo', 'Rosa', 'Ian', 'Faye', 'Dean', 'Kira', 'Paul', 'Jade'];
const LASTS = ['Reyes', 'Barnes', 'Cole', 'Ford', 'Lin', 'Russo', 'Pena', 'Shah', 'Monroe', 'Webb'];

export function buildPosted(): Posting[] {
  const today: Posting[] = [
    posting({ id: 'PD-A', patient: 'Aisha Patel', payer: CIGNA, amount: 210, mday: 16, h: 9, m: 58, dateLabel: 'Today', day: 'today' }),
    posting({ id: 'PD-B', patient: 'Ben Carter', payer: DELTA, amount: 388, mday: 16, h: 9, m: 31, dateLabel: 'Today', day: 'today' }),
    posting({ id: 'PD-C', patient: 'Lucy Ortiz', payer: GUARDIAN, amount: 142, mday: 16, h: 9, m: 5, dateLabel: 'Today', day: 'today' }),
  ];
  // Yesterday (Oct 15), newest first.
  const yTimes: [number, number][] = [[16, 2], [15, 36], [14, 10], [12, 48], [11, 22], [10, 40], [9, 15]];
  const yesterday: Posting[] = [];
  for (let i = 0; i < 7; i++) {
    const amt = [312, 188, 276, 404, 142, 266.4, 324][i] ?? 200;
    const t = yTimes[i] ?? [9, 0];
    yesterday.push(
      posting({
        id: `PD-Y${String(i)}`,
        patient: `${FIRST[i] ?? 'Pat'} ${LASTS[i % LASTS.length] ?? 'Doe'}`,
        payer: PAYERS[i % PAYERS.length] ?? DELTA,
        amount: amt,
        mday: 15,
        h: t[0],
        m: t[1],
        dateLabel: 'Yesterday',
        day: 'yesterday',
        ...(i === 2 ? { by: RYAN } : {}),
        ...(i === 5 ? { how: 'api' as const } : {}),
      }),
    );
  }
  // Earlier in October (Oct 14 → Oct 3), newest first.
  const earlier: Posting[] = [];
  for (let i = 0; i < 25; i++) {
    const amt = Math.round((180 + (i * 37) % 520) * 100) / 100;
    const mday = 14 - (i % 12); // 14..3
    const h = 9 + (i % 8);
    const m = (i * 13) % 60;
    earlier.push(
      posting({
        id: `PD-E${String(i)}`,
        patient: `${FIRST[(i + 7) % FIRST.length] ?? 'Pat'} ${LASTS[(i + 3) % LASTS.length] ?? 'Doe'}`,
        payer: PAYERS[(i + 2) % PAYERS.length] ?? DELTA,
        amount: amt,
        mday,
        h,
        m,
        dateLabel: `Oct ${String(mday)}`,
        day: 'earlier',
        ...(i < 5 ? { by: RYAN } : {}),
        ...(i === 8 ? { how: 'api' as const } : {}),
      }),
    );
  }
  // Ben Carter's undone first posting (Oct 9) + its Undo-of row (Oct 15).
  earlier.push(
    posting({
      id: 'PD-BEN9', patient: 'Ben Carter', payer: DELTA, amount: 388, mday: 9, h: 14, m: 20, dateLabel: 'Oct 9', day: 'earlier',
      undone: true, claimOf: 'the wrong visit',
      reverseNote: `Undone on Oct 15. ELVA reversed it in ${PMS} and put the $388.00 back in To post.`,
    }),
    posting({
      id: 'PD-UNDO-BEN9', patient: 'Ben Carter', payer: DELTA, amount: 388, mday: 15, h: 16, m: 5, dateLabel: 'Oct 15', day: 'earlier',
      isUndo: true,
      reverseNote: 'Reversal of Ben Carter’s $388.00 posting of Oct 9 (the wrong visit).',
    }),
  );
  // Jorge Martinez's $486.00 — the posting ELVA's log shows at 10:42 AM (so "Open in Posting › Posted" finds it).
  earlier.push(
    posting({
      id: 'PD-JORGE', patient: 'Jorge Martinez', payer: DELTA, amount: 486, mday: 15, h: 10, m: 42, dateLabel: 'Oct 15', day: 'earlier',
      claimOf: 'Sep 22',
      lines: [line('D2740', '#3', 'porcelain crown', 1180, 972, 486)],
    }),
  );
  return [...today, ...yesterday, ...earlier];
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A Posted-history row (the searchable list) opened as a full posting, so every
 * row in Posted is clickable and routable at /posting/posted/:id (D202). The
 * detail is rebuilt deterministically from the row: nothing here is invented
 * beyond what the row says (patient, payer, amount, date, trace).
 */
export function postingFromHistoryRow(r: PostedHistoryRow): Posting {
  const payerShort = r.payer.replace(/ Dental of Texas$/, '').split(' ')[0] ?? r.payer;
  const seed = seedOf(`${r.patient}|${r.payer}|${r.id}`);
  const month = Number(r.dateIso.slice(5, 7));
  const day = Number(r.dateIso.slice(8, 10));
  const dateDisp = `${MONTH_ABBR[month - 1] ?? ''} ${String(day)}`;
  const amt = Math.abs(r.amount);
  const lines =
    r.amount < 0
      ? [line('D2740', undefined, 'takeback of an earlier payment', 0, 0, r.amount)]
      : [line('D2740', '#3', 'porcelain crown', round2(amt * 2), round2(amt * 1.4), amt)];
  const feeSum = round2(lines.reduce((t, l) => t + l.fee, 0));
  const owesSum = round2(lines.reduce((t, l) => t + l.owes, 0));
  const trace = r.trace || traceFor(seed);
  const by: Posting['confirmedBy'] =
    r.confirmedBy === 'Autopilot' ? 'autopilot' : r.confirmedBy === 'Ryan Cole' ? RYAN : AVA;
  const payment: PaymentRef =
    r.method === 'check'
      ? { method: 'check', ...(r.checkNo ? { checkNo: r.checkNo } : {}) }
      : r.method === 'card'
        ? { method: 'card', last4: r.last4 ?? String(1000 + (Math.abs(seed) % 9000)), approval: r.approval ?? String(100000 + (Math.abs(seed) % 899999)) }
        : { method: 'eft', trace };
  const already = r.state === 'already_posted';
  const isCard = r.method === 'card';
  return {
    id: r.id,
    patient: r.patient,
    firstName: r.patient.split(' ')[0] ?? r.patient,
    payer: { name: r.payer, short: payerShort },
    memberId: memberFor(payerShort, seed),
    claimOf: r.claimNumber ? `claim ${r.claimNumber}` : 'this claim',
    dentist: DR_CARTER,
    payment,
    amount: r.amount,
    amountLabel: `${payerShort} paid`,
    postedTime: clock(9 + (Math.abs(seed) % 8), Math.abs(seed >> 3) % 60),
    postedDateLabel: dateDisp,
    postedIso: `${r.dateIso}T10:00:00`,
    postedDay: 'earlier',
    eraDisplay: dateDisp,
    trace,
    confirmedBy: by,
    how: 'agent',
    lines,
    balanceBefore: feeSum,
    balanceAfter: owesSum,
    howFound: already
      ? `Someone posted this payment in ${PMS} before ELVA did. ELVA linked it and didn’t post again.`
      : `${payerShort} paid the claim; ELVA matched it and posted it in ${PMS}.`,
    sources: [{ id: `src-${r.id}`, label: `ERA of ${dateDisp}` }],
    log: already
      ? [
          { time: '10:41:58', text: `Looked for the ${payerShort} payment in ${PMS} before posting` },
          { time: '10:42:06', text: `Found it already posted in ${PMS} by your team` },
          { time: '10:42:09', text: 'Linked it to the ERA. Nothing was written' },
        ]
      : genLog(payerShort, dateDisp, r.amount),
    durationSec: already ? 11 : 21,
    // Card payments settle through the card processor, never a bank deposit: no "Matched" (D195, D252-era review).
    depositNote: isCard
      ? cardChargedLine(payment.approval ?? '')
      : already
        ? `${payerShort}’s deposit isn’t matched to this posting by ELVA.`
        : `Part of a ${payerShort} deposit on ${dateDisp}. Open Matched to see the deposit.`,
    statementCovers: !isCard && !already,
    history: already ? [`already posted in ${PMS} by your team`, `linked by ELVA on ${dateDisp}`] : [`posted by ELVA on ${dateDisp}`],
    ...(already ? { alreadyPosted: true } : {}),
  };
}

export const POSTED_GROUP_TOTALS = {
  yesterday: { count: 7, total: 1912.4 },
  earlier: { count: 27, total: 6707.8 },
} as const;

// ---------------------------------------------------------------------------
// Autopilot (§12.3)
// ---------------------------------------------------------------------------

export const AUTOPILOT = {
  since: 'Nov 3',
  by: 'Ava Thompson',
  today: 'Nov 16',
  postedToday: { count: 11, total: 3184.6 },
  postedMonth: 164,
  waiting: { count: 3, total: 864 },
} as const;

export function buildAutopilotRows(): AutopilotRow[] {
  const mins = seedTrainingKinds();
  const done = (id: string): number => mins.find((k) => k.id === id)?.confirmed ?? 0;
  const held = (id: string, label: string): string =>
    `Not enough confirmed yet: ELVA has posted ${String(done(id))} ${label} you confirmed. It needs ${String(KIND_MINIMUM)} before it posts them on its own.`;
  return [
    { id: 'AP-1', patient: 'Nora Adams', payer: 'Delta Dental of Texas', amount: 512, why: 'A decision is needed: Nora’s visits of Oct 13 and Oct 20 have the same procedure and fee, and the ERA doesn’t say which one.' },
    { id: 'AP-2', patient: 'Ethan Walsh', payer: 'Cigna', amount: 380, why: 'A decision is needed: Cigna paid a cheaper crown than billed. Post it as the plan allows, or appeal.' },
    { id: 'AP-3', patient: 'Linda Park', payer: 'Delta Dental of Texas', amount: -28, why: 'Not enough confirmed yet: ELVA has posted 2 takebacks you confirmed. It needs 5 before it posts them on its own.' },
    { id: 'AP-4', patient: 'Iris Calder', payer: 'MetLife', amount: 0, why: held('paid_to_patient', 'payments the payer sent to the patient') },
    { id: 'AP-5', patient: 'Hugo Brandt', payer: 'Guardian', amount: 612, why: held('patient_credit', 'payments that leave a patient credit') },
  ];
}

/** To-post items for the autopilot scenario (the 3 held). */
export function buildAutopilotToPost(): PostingItem[] {
  const base = buildToPost();
  const pick = (id: string): PostingItem => {
    const f = base.find((b) => b.id === id);
    if (!f) throw new Error(`missing fixture ${id}`);
    return f;
  };
  const nora: PostingItem = {
    ...pick('PO-1'),
    id: 'AP-1',
    patient: 'Nora Adams',
    firstName: 'Nora',
    amount: 512,
    reason: 'Two claims fit this payment',
    listMeta: 'ERA of Nov 16 · today',
    whatHappened: { headline: 'Two claims fit this payment', body: 'Nora’s visits of Oct 13 and Oct 20 have the same procedure and fee, and the ERA doesn’t say which one.' },
  };
  const ethan = { ...pick('PO-2'), listMeta: 'ERA of Nov 13 · 3 days' };
  const linda = { ...pick('PO-3'), listMeta: 'ERA of Nov 12 · 4 days' };
  // Two kinds ELVA has not posted 5 of yet (H1 §6): they come back to a person on autopilot.
  const heldQuestion = { text: 'Post it this way?', options: [{ id: 'yes', label: 'Yes, post it this way', effect: 'enable_post' as const }] };
  const iris: PostingItem = { ...pick('PO-IRIS'), id: 'AP-4', group: 'needs_you', question: heldQuestion };
  const hugo: PostingItem = { ...pick('PO-HUGO'), id: 'AP-5', group: 'needs_you', question: heldQuestion };
  return [nora, ethan, linda, iris, hugo];
}

// ---------------------------------------------------------------------------
// Bank deposits (§12.4, revised choices §6)
// ---------------------------------------------------------------------------

export function buildDifferences(): BankDifference[] {
  return [
    // money in, nothing posted — ELVA handled it (no choices)
    {
      id: 'BD-1',
      kind: 'money_no_post',
      payer: 'Delta Dental of Texas',
      diff: 1312.4,
      dateLine: 'Oct 9 deposit · trace 1T740588190',
      headline: 'The money is in the bank, but nothing was posted for it',
      body: 'A deposit of $1,312.40 from Delta landed Oct 9 with no ERA posted. The morning after, ELVA fetched the remittance itself.',
      handled: {
        ticks: [
          { text: 'Oct 10, 7:10 AM · signed in to Delta’s provider portal and downloaded the remittance for this deposit', links: [{ label: 'The remittance' }, { label: 'See the portal visit in ELVA’s log' }] },
          { text: 'Its 5 claims are now in To post, Sep 15–Sep 30.' },
        ],
        openClaims: {
          summary: 'Open the 5 claims in To post',
          list: [
            { id: 'oc-1', patient: 'Hannah Cole', date: 'Sep 15', amount: 286.0 },
            { id: 'oc-2', patient: 'Isaac Ford', date: 'Sep 19', amount: 190.4 },
            { id: 'oc-3', patient: 'Grace Lin', date: 'Sep 23', amount: 342.0 },
            { id: 'oc-4', patient: 'Leo Barnes', date: 'Sep 27', amount: 248.0 },
            { id: 'oc-5', patient: 'Mia Russo', date: 'Sep 30', amount: 246.0 },
          ],
        },
      },
      choices: [],
    },
    // two open payments could fit one deposit — a person picks (rcm-32 §7)
    {
      id: 'BD-5',
      kind: 'two_fit',
      payer: 'Cigna',
      diff: 642.0,
      dateLine: 'Oct 14 deposit $642.00 · Chase ··4417',
      headline: 'Two Cigna payments could fit this deposit',
      body: 'Cigna’s bank line shows no trace number, and two open Cigna payments are each $642.00. ELVA matches by amount and date only when exactly one payment fits, so this deposit waits here for a person.',
      breakdown: [
        { label: 'Deposit of Oct 14', amount: 642.0 },
        { label: 'Open payment: Daniel Cruz, ERA of Oct 8', amount: 642.0 },
        { label: 'Open payment: Ellie Ramos, ERA of Oct 9', amount: 642.0 },
      ],
      metaLine: 'Both payments arrived within 3 business days of the deposit.',
      prompt: 'Which payment is this deposit?',
      choices: [
        { id: 'pick_a', label: 'Daniel Cruz’s payment, ERA of Oct 8', effect: 'pick_a' },
        { id: 'pick_b', label: 'Ellie Ramos’s payment, ERA of Oct 9', effect: 'pick_b' },
        { id: 'self', label: 'Neither. I’ll sort it out myself', effect: 'self' },
      ],
    },
    // takeback — post the fix / dispute
    {
      id: 'BD-3',
      kind: 'takeback',
      payer: 'MetLife',
      diff: -23.88,
      dateLine: 'Oct 13 deposit $1,186.12 · ERA of Oct 12, 1 claim, $1,210.00',
      headline: 'MetLife kept back $23.88 for an older claim',
      body: 'MetLife’s ERA of Oct 12 was $1,210.00 for Owen Barnes’s claim, but it deposited $1,186.12 — it kept back $23.88 against an older claim.',
      breakdown: [
        { label: 'ERA paid (Owen Barnes)', amount: 1210.0 },
        { label: 'Taken back', amount: -23.88, kind: 'neg' },
        { label: 'Deposited', amount: 1186.12, kind: 'total' },
      ],
      check: {
        ok: true,
        text: 'MetLife is right. It overpaid $23.88 on Noah Kim’s Aug 12 claim (D2392: paid $135.88; allowed $140.00 at 80% = $112.00), and is taking it back now.',
      },
      metaLine: 'PLB takeback WO · Noah Kim, Aug 12 · trace 1T93120477',
      choices: [
        { id: 'post', label: 'Post the takeback on Noah’s claim', effect: 'post_fix', primary: true },
        { id: 'dispute', label: 'Dispute it with MetLife', effect: 'dispute' },
      ],
      log: [
        { time: '2:14:02', text: `Opened Noah Kim’s Aug 12 claim in ${PMS}` },
        { time: '2:14:08', text: 'Posted an insurance adjustment of −$23.88' },
        { time: '2:14:14', text: 'Checked: Noah’s balance went up by $23.88' },
      ],
    },
    // interest — post / self
    {
      id: 'BD-4',
      kind: 'interest',
      payer: 'Aetna',
      diff: 4.18,
      dateLine: 'Oct 7 deposit $604.18 · ERA of Oct 6, $600.00',
      headline: 'Aetna added $4.18 of interest for paying late',
      body: 'Aetna owed $600.00 on Priya Shah’s claim of Jul 21 and paid late, so it added $4.18 of interest. ELVA posts it as interest income, not on the patient’s account.',
      breakdown: [
        { label: 'ERA paid (Priya Shah)', amount: 600.0 },
        { label: 'Interest for paying late', amount: 4.18 },
        { label: 'Deposited', amount: 604.18, kind: 'total' },
      ],
      metaLine: 'PLB interest L6 · Priya Shah, Jul 21 · trace 1T551209763',
      choices: [
        { id: 'post', label: 'Post the interest', effect: 'post_fix', primary: true },
        { id: 'self', label: 'I’ll post it myself', effect: 'self' },
      ],
      log: [
        { time: '2:20:01', text: `Opened the practice’s interest-income account in ${PMS}` },
        { time: '2:20:07', text: 'Posted $4.18 of interest income' },
        { time: '2:20:11', text: 'Checked: the deposit now balances' },
      ],
    },
  ];
}
